import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./use-auth";

export interface Permission {
  id: string;
  key: string;
  name: string;
  description: string;
  resource: string;
  action: string;
}

export function usePermissions() {
  const { user, isAuthenticated } = useAuth();
  
  // Role-based permissions are the sole mechanism for access control
  
  const { data: permissions = [], isLoading } = useQuery<Permission[]>({
    queryKey: [`/api/users/${user?.id}/permissions`],
    enabled: !!user?.id && isAuthenticated,
    retry: false,
  });

  const hasPermission = (permissionKey: string): boolean => {
    return permissions.some(permission => permission.key === permissionKey);
  };

  const hasResourceAction = (resource: string, action: string): boolean => {
    return permissions.some(permission => 
      permission.resource === resource && permission.action === action
    );
  };

  return {
    permissions,
    isLoading,
    hasPermission,
    hasResourceAction,
    isViewer: false,
    
    // Claims permissions
    canViewAssignedClaims: hasPermission('claims.read_assigned'),
    canViewTeamClaims: hasPermission('claims.read_team'),
    canViewAllClaims: hasPermission('claims.read_all'),
    canUpdateAssignedClaims: hasPermission('claims.write_assigned'),
    canUpdateAllClaims: hasPermission('claims.write_all'),
    canAssignClaims: hasPermission('claims.assign'),
    canDeleteClaims: hasPermission('claims.delete'),
    canCloseClaims: hasPermission('claims.close'),
    
    // Documents permissions
    canViewAssignedDocuments: hasPermission('documents.read_assigned'),
    canViewAllDocuments: hasPermission('documents.read_all'),
    canWriteDocuments: hasPermission('documents.write'),
    canDeleteDocuments: hasPermission('documents.delete'),
    canViewSensitiveDocuments: hasPermission('documents.read_sensitive'),
    
    // Communications permissions
    canViewAssignedCommunications: hasPermission('communications.read_assigned'),
    canViewAllCommunications: hasPermission('communications.read_all'),
    canSendCommunications: hasPermission('communications.send'),
    
    // Financial permissions - Commissions
    canViewOwnCommissions: hasPermission('finances.view_claim_commission_self'),
    canViewTeamCommissions: hasPermission('finances.view_claim_commission_team'),
    canViewAllCommissions: hasPermission('finances.view_claim_commission_all'),
    canEditCommissions: hasPermission('finances.edit_claim_commission'),
    canApproveCommissions: hasPermission('finances.approve_commissions'),
    
    // Financial permissions - Company & Carrier Checks
    canViewCompanyFinances: hasPermission('finances.view_company'),
    canManageCompanyFinances: hasPermission('finances.manage_company'),
    canViewCarrierChecks: hasPermission('finances.view_carrier_checks'),
    canManageCarrierChecks: hasPermission('finances.manage_carrier_checks'),
    
    // Reports permissions
    canViewOperationalReports: hasPermission('reports.view_operational'),
    canViewFinancialReports: hasPermission('reports.view_financial'),
    canViewPerformanceReports: hasPermission('reports.view_performance'),
    canExportReports: hasPermission('reports.export'),
    
    // Users permissions
    canViewUserDirectory: hasPermission('users.view_directory'),
    canViewUserProfile: hasPermission('users.view_profile'),
    canEditUserProfile: hasPermission('users.edit_profile'),
    canInviteUsers: hasPermission('users.invite'),
    canDeactivateUsers: hasPermission('users.deactivate'),
    canManageRoles: hasPermission('users.manage_roles'),
    
    // Contacts permissions
    canReadContacts: hasPermission('contacts.read'),
    canWriteContacts: hasPermission('contacts.write'),
    canDeleteContacts: hasPermission('contacts.delete'),
    
    // AI permissions
    canUseDraftingAI: hasPermission('ai.use_drafting'),
    canViewAITrainingData: hasPermission('ai.view_training_data'),
    canManageAITrainingData: hasPermission('ai.manage_training_data'),
    canManageAIModels: hasPermission('ai.manage_models'),
    
    // Leads permissions
    canReadLeads: hasPermission('leads.read'),
    canWriteLeads: hasPermission('leads.write'),
    canManualOverrideLeads: hasPermission('leads.manual_override'),
    
    // Settings permissions
    canViewSettings: hasPermission('settings.view'),
    canManageSettings: hasPermission('settings.manage'),
    canManageIntegrations: hasPermission('settings.manage_integrations'),
    canManagePermissions: hasPermission('settings.manage_permissions'),
    
    // Audit permissions
    canViewAuditLogs: hasPermission('audit.view'),
    
    // Search permissions
    canExecuteSearch: hasPermission('search.execute'),
    
    // Dashboard permissions
    canViewDashboardOverview: hasPermission('dashboard.overview.read'),
    canViewDashboardAdjuster: hasPermission('dashboard.adjuster.read'),
    
    // Helper methods for common multi-scope checks
    canViewClaims: hasPermission('claims.read_assigned') || hasPermission('claims.read_team') || hasPermission('claims.read_all'),
    canUpdateClaims: hasPermission('claims.write_assigned') || hasPermission('claims.write_all'),
    canViewDocuments: hasPermission('documents.read_assigned') || hasPermission('documents.read_all'),
    canViewCommunications: hasPermission('communications.read_assigned') || hasPermission('communications.read_all'),
    canViewCommissions: hasPermission('finances.view_claim_commission_self') || hasPermission('finances.view_claim_commission_team') || hasPermission('finances.view_claim_commission_all'),
    canViewReports: hasPermission('reports.view_operational') || hasPermission('reports.view_financial') || hasPermission('reports.view_performance'),
    
    // Backwards compatibility aliases for existing code
    canReadClaims: hasPermission('claims.read_assigned') || hasPermission('claims.read_team') || hasPermission('claims.read_all'),
    canReadAllClaims: hasPermission('claims.read_all'),
    canReadAssignedClaims: hasPermission('claims.read_assigned'),
    canWriteClaims: hasPermission('claims.write_assigned') || hasPermission('claims.write_all'),
    canReadUsers: hasPermission('users.view_directory') || hasPermission('users.view_profile'),
    canWriteUsers: hasPermission('users.edit_profile') || hasPermission('users.invite'),
    canAdminUsers: hasPermission('users.manage_roles'),
    canReadFinances: hasPermission('finances.view_company') || hasPermission('finances.view_claim_commission_self'),
    canWriteFinances: hasPermission('finances.manage_company') || hasPermission('finances.edit_claim_commission'),
    canAdminReports: hasPermission('reports.view_financial') || hasPermission('reports.view_performance'),
    
    // Admin checks
    isAdmin: hasPermission('users.manage_roles') || hasPermission('settings.manage_permissions'),
    isSuperAdmin: !!(user as any)?.isSuperAdmin,
    
    // Legacy compatibility for old permission key patterns (maps to canonical keys)
    canCreateClaims: hasPermission('claims.write_assigned'),
    canExportClaims: hasPermission('reports.export'),
    canCreateDocuments: hasPermission('documents.write'),
    canUpdateDocuments: hasPermission('documents.write'),
    canOrganizeDocuments: hasPermission('documents.write'),
    canAnalyzeDocuments: hasPermission('ai.use_drafting'),
    canChatWithDocuments: hasPermission('ai.use_drafting'),
    canCreateCommunications: hasPermission('communications.send'),
    canUpdateCommunications: hasPermission('communications.send'),
    canDeleteCommunications: hasPermission('communications.delete'),
    canViewOwnExpenses: hasPermission('finances.view_company'),
    canViewTeamExpenses: hasPermission('finances.view_company'),
    canViewAllExpenses: hasPermission('finances.view_company'),
    canCreateExpenses: hasPermission('finances.manage_company'),
    canUpdateExpenses: hasPermission('finances.manage_company'),
    canDeleteExpenses: hasPermission('finances.manage_company'),
    canCalculateCommissions: hasPermission('finances.edit_claim_commission'),
    canViewCompanyExpenses: hasPermission('finances.view_company'),
    canCreateCompanyExpenses: hasPermission('finances.manage_company'),
    canUpdateCompanyExpenses: hasPermission('finances.manage_company'),
    canCreateCarrierChecks: hasPermission('finances.manage_carrier_checks'),
    canUpdateCarrierChecks: hasPermission('finances.manage_carrier_checks'),
    canAccessQuickBooks: hasPermission('settings.manage_integrations'),
    canSyncQuickBooks: hasPermission('settings.manage_integrations'),
    canViewAssignedContacts: hasPermission('contacts.read'),
    canViewTeamContacts: hasPermission('contacts.read'),
    canViewAllContacts: hasPermission('contacts.read'),
    canCreateContacts: hasPermission('contacts.write'),
    canUpdateContacts: hasPermission('contacts.write'),
    canExtractContacts: hasPermission('ai.use_drafting'),
    canUseDraftAI: hasPermission('ai.use_drafting'),
    canUseClassificationAI: hasPermission('ai.use_drafting'),
    canUsePredictionAI: hasPermission('ai.use_drafting'),
    canUseChatAI: hasPermission('ai.use_drafting'),
    canAccessAITraining: hasPermission('ai.view_training_data'),
    canManageAITraining: hasPermission('ai.manage_training_data'),
    canUseDocumentOCR: hasPermission('ai.use_drafting'),
    canUseDamageIdentification: hasPermission('ai.use_drafting'),
    canViewOwnReports: hasPermission('reports.view_operational'),
    canViewTeamReports: hasPermission('reports.view_operational'),
    canViewGlobalReports: hasPermission('reports.view_financial') || hasPermission('reports.view_performance'),
    canViewRevenueReports: hasPermission('reports.view_financial'),
    canViewUsers: hasPermission('users.view_directory'),
    canCreateUsers: hasPermission('users.invite'),
    canUpdateUsers: hasPermission('users.edit_profile'),
    canDeleteUsers: hasPermission('users.deactivate'),
    canViewAllUsers: hasPermission('users.view_directory'),
    canUpdateSettings: hasPermission('settings.manage'),
    canManageSystemSettings: hasPermission('settings.manage'),
    canExportAuditLogs: hasPermission('audit.view'),
    canViewAssignedTasks: hasPermission('claims.read_assigned'),
    canViewTeamTasks: hasPermission('claims.read_team'),
    canViewAllTasks: hasPermission('claims.read_all'),
    canCreateTasks: hasPermission('claims.write_assigned'),
    canUpdateTasks: hasPermission('claims.write_assigned'),
    canEditTask: (task: { createdBy?: string | null; assignedTo?: string | null }) => {
      // Assignee can always edit their own task, regardless of role
      if (task.assignedTo === user?.id) return true;
      if (!hasPermission('claims.write_assigned') && !hasPermission('claims.write_all')) return false;
      if (hasPermission('claims.write_all') || hasPermission('users.manage_roles') || hasPermission('settings.manage_permissions')) return true;
      return task.createdBy === user?.id;
    },
    canDeleteTasks: hasPermission('claims.write_all'),
    canAssignTasks: hasPermission('claims.assign'),
    canViewInspections: hasPermission('claims.read_assigned'),
    canCreateInspections: hasPermission('claims.write_assigned'),
    canUpdateInspections: hasPermission('claims.write_assigned'),
    canDeleteInspections: hasPermission('claims.write_all'),
    canViewLeads: hasPermission('leads.read'),
    canCreateLeads: hasPermission('leads.write'),
    canUpdateLeads: hasPermission('leads.write'),
    canDeleteLeads: hasPermission('leads.write'),
    canViewTasks: hasPermission('claims.read_assigned') || hasPermission('claims.read_team') || hasPermission('claims.read_all'),
    canViewContacts: hasPermission('contacts.read'),
    canViewExpenses: hasPermission('finances.view_company'),
  };
}
